import React, { useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { PLATFORMS, PROVIDERS, EVENT_TYPE_OPTIONS, DEVICE_OPTIONS, ALL_GAMES } from "./catalog";
import { RotateCcw, SlidersHorizontal } from "lucide-react";

export const PRESETS = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "all", label: "Tudo" },
  { value: "custom", label: "Período" },
];

export function computeRange(preset, customFrom, customTo) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  if (preset === "today") return { from: today.toISOString(), to: now.toISOString() };
  if (preset === "yesterday") return { from: daysAgo(1).toISOString(), to: today.toISOString() };
  if (preset === "7d") return { from: daysAgo(6).toISOString(), to: now.toISOString() };
  if (preset === "30d") return { from: daysAgo(29).toISOString(), to: now.toISOString() };
  if (preset === "custom") {
    return {
      from: customFrom ? new Date(`${customFrom}T00:00:00`).toISOString() : null,
      to: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : null,
    };
  }
  return { from: null, to: null };
}

// Period and entity filters are shared between admin pages for the browser session.
const SHARED_KEY = "mia_admin_filter";
const SHARED_FIELDS = ["preset", "customFrom", "customTo", "platform", "provider", "game", "campaign"];

export function useSharedFilter(defaults) {
  const [filter, setFilterState] = useState(() => {
    const base = defaults();
    try {
      const saved = JSON.parse(sessionStorage.getItem(SHARED_KEY) || "{}");
      const merged = { ...base };
      SHARED_FIELDS.forEach((k) => { if (k in saved && k in base) merged[k] = saved[k]; });
      if (saved.preset) Object.assign(merged, computeRange(merged.preset, merged.customFrom, merged.customTo));
      return merged;
    } catch (e) {
      return base;
    }
  });
  const setFilter = useCallback((next) => {
    setFilterState(next);
    try {
      const saved = JSON.parse(sessionStorage.getItem(SHARED_KEY) || "{}");
      SHARED_FIELDS.forEach((k) => { if (k in next) saved[k] = next[k]; });
      sessionStorage.setItem(SHARED_KEY, JSON.stringify(saved));
    } catch (e) {}
  }, []);
  return [filter, setFilter];
}

let optionsCache = null;
function useFilterOptions(enabled) {
  const [opts, setOpts] = useState(optionsCache || { campaigns: [], sources: [] });
  useEffect(() => {
    if (!enabled || optionsCache) return;
    api.get("/admin/filter-options").then(({ data }) => { optionsCache = data; setOpts(data); }).catch(() => {});
  }, [enabled]);
  return opts;
}

function Select({ testid, value, onChange, label, children }) {
  return (
    <select aria-label={label} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full lg:w-auto lg:max-w-[180px] min-w-0">
      {children}
    </select>
  );
}

export default function FilterBar({ value, onChange, show = {} }) {
  const cfg = { platform: true, provider: true, game: false, device: true, type: false, campaign: false, source: false, compare: false, ...show };
  const opts = useFilterOptions(cfg.campaign || cfg.source);
  const set = (patch) => onChange({ ...value, ...patch });
  const onPreset = (preset) => set({ preset, ...computeRange(preset, value.customFrom, value.customTo) });
  const onCustom = (key, v) => {
    const next = { ...value, [key]: v, preset: "custom" };
    onChange({ ...next, ...computeRange("custom", next.customFrom, next.customTo) });
  };
  const [open, setOpen] = useState(false);
  const active = ["platform", "provider", "game", "device", "event_type", "campaign", "source"].filter((k) => value[k]).length;
  const reset = () => onChange({
    preset: "30d", ...computeRange("30d"), platform: "", provider: "", game: "", device: "",
    event_type: "", campaign: "", source: "", customFrom: "", customTo: "", compare: value.compare,
  });

  return (
    <div className="flex flex-wrap items-center gap-2 pb-4 mb-5 border-b border-[var(--border)]" data-testid="filter-bar">
      <div className="ops-seg ops-seg-scroll" role="group" aria-label="Período">
        {PRESETS.map((p) => (
          <button key={p.value} type="button" data-testid={`preset-${p.value}`} aria-pressed={value.preset === p.value} onClick={() => onPreset(p.value)}>{p.label}</button>
        ))}
      </div>
      {value.preset === "custom" && (
        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <input type="date" aria-label="Data inicial" data-testid="custom-from" className="mia-input !h-8 !py-0 flex-1 sm:!w-auto text-xs" value={value.customFrom || ""} onChange={(e) => onCustom("customFrom", e.target.value)} />
          <span className="text-[var(--muted2)] text-xs">até</span>
          <input type="date" aria-label="Data final" data-testid="custom-to" className="mia-input !h-8 !py-0 flex-1 sm:!w-auto text-xs" value={value.customTo || ""} onChange={(e) => onCustom("customTo", e.target.value)} />
        </div>
      )}
      {cfg.compare && (
        <label className="inline-flex items-center gap-2 min-h-[38px] text-[12px] text-[var(--muted)] cursor-pointer select-none">
          <input type="checkbox" data-testid="filter-compare" checked={!!value.compare} onChange={(e) => set({ compare: e.target.checked })} className="accent-[var(--accent)]" />
          Comparar com período anterior
        </label>
      )}

      <button type="button" className="lg:hidden ops-btn ops-btn-outline ops-btn-sm ml-auto" onClick={() => setOpen((o) => !o)}
        aria-expanded={open} aria-controls="filter-panel" data-testid="filter-toggle">
        <SlidersHorizontal size={14} /> Filtros
        {active > 0 && <span className="mono text-[11px] px-1.5 rounded-[3px] bg-[var(--accent-weak)] text-[var(--accent-ink)]" data-testid="filter-active-count">{active}</span>}
      </button>
      <div id="filter-panel" className={`${open ? "grid" : "hidden"} grid-cols-2 gap-2 w-full lg:flex lg:flex-wrap lg:items-center lg:gap-1.5 lg:w-auto lg:ml-auto`} data-testid="filter-panel">
        {cfg.platform && (
          <Select testid="filter-platform" label="Plataforma" value={value.platform} onChange={(v) => set({ platform: v })}>
            <option value="">Plataforma: todas</option>
            {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </Select>
        )}
        {cfg.provider && (
          <Select testid="filter-provider" label="Provedor" value={value.provider} onChange={(v) => set({ provider: v })}>
            <option value="">Provedor: todos</option>
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        )}
        {cfg.game && (
          <Select testid="filter-game" label="Jogo" value={value.game} onChange={(v) => set({ game: v })}>
            <option value="">Jogo: todos</option>
            {ALL_GAMES.map((g) => <option key={`${g.provider}-${g.id}`} value={g.id}>{g.name} · {g.providerName}</option>)}
          </Select>
        )}
        {cfg.campaign && (
          <Select testid="filter-campaign" label="Campanha" value={value.campaign} onChange={(v) => set({ campaign: v })}>
            <option value="">Campanha: todas</option>
            {opts.campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
        {cfg.source && (
          <Select testid="filter-source" label="Origem" value={value.source} onChange={(v) => set({ source: v })}>
            <option value="">Origem: todas</option>
            {opts.sources.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
        {cfg.device && (
          <Select testid="filter-device" label="Dispositivo" value={value.device} onChange={(v) => set({ device: v })}>
            {DEVICE_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.value ? d.label : "Dispositivo: todos"}</option>)}
          </Select>
        )}
        {cfg.type && (
          <Select testid="filter-type" label="Tipo de evento" value={value.event_type} onChange={(v) => set({ event_type: v })}>
            {EVENT_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        )}
        <button type="button" data-testid="filter-reset" onClick={reset} title="Limpar filtros" aria-label="Limpar filtros" className="ops-btn ops-btn-ghost ops-btn-sm col-span-2 lg:col-span-1 border !border-[var(--border)] lg:!border-transparent">
          <RotateCcw size={13} /> <span className="lg:hidden">Limpar filtros</span>
        </button>
      </div>
    </div>
  );
}
